import React, { useRef, useState, useEffect } from 'react';
import './StickyFeatures.css';

/* SVG icon components — clean line-art style */
const icons = {
  server: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="6" rx="2"/><rect x="2" y="15" width="20" height="6" rx="2"/>
      <line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>
    </svg>
  ),
  layout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="9" y1="21" x2="9" y2="9"/>
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      <circle cx="12" cy="16" r="1"/>
    </svg>
  ),
  mic: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  ),
  bot: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="12" rx="2"/><path d="M12 2v6"/>
      <circle cx="8" cy="14" r="1.5"/><circle cx="16" cy="14" r="1.5"/>
      <path d="M9 18h6"/>
    </svg>
  ),
  bulb: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6"/><path d="M10 22h4"/>
      <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/>
    </svg>
  )
};

const features = [
  {
    id: 1,
    icon: 'server',
    badge: 'Web · Full-Stack',
    title: 'Microlab Inventory System',
    desc: 'Tracks item borrowing in a lab — records borrower name, item details, and expected return date for equipment management using JS, HTML/CSS, and Firebase.',
    color: '#667eea',
    accent: '#8b5cf6'
  },
  {
    id: 2,
    icon: 'layout',
    badge: 'Web · Frontend',
    title: 'Restaurant Web App',
    desc: 'Fully functional restaurant web application with menu display and ordering flow built using vanilla web technologies (HTML, CSS, JavaScript).',
    color: '#764ba2',
    accent: '#a855f7'
  },
  {
    id: 3,
    icon: 'lock',
    badge: 'Embedded · ESP32',
    title: 'Smart Room Access System',
    desc: 'Monitors and logs room entry and exit using keypad authentication, I2C LCD, servo motor, and TM1637 display powered by MicroPython.',
    color: '#10b981',
    accent: '#34d399'
  },
  {
    id: 4,
    icon: 'mic',
    badge: 'DSP · ESP32',
    title: 'Voice-Controlled LED with FFT',
    desc: 'LED lighting controlled by voice commands using FFT-based audio frequency filtering for accurate signal detection on the ESP32 platform.',
    color: '#f43f5e',
    accent: '#fb7185'
  },
  {
    id: 5,
    icon: 'bot',
    badge: 'Embedded System',
    title: 'Sumo Bot',
    desc: 'Arduino-powered sumo robot with wireless control via ESP32 and a custom phone-based Bluetooth controller app.',
    color: '#f59e0b',
    accent: '#fbbf24'
  },
  {
    id: 6,
    icon: 'bulb',
    badge: 'Feedback Control',
    title: 'Ambient Light Smart Lighting',
    desc: 'A feedback-controlled system that automatically adjusts LED brightness based on ambient light levels using LDR sensors, PWM output, and closed-loop control logic.',
    color: '#3b82f6',
    accent: '#60a5fa'
  }
];

export default function StickyFeatures() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFixed, setIsFixed] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const vh = window.innerHeight;

      if (rect.top > 0) {
        setIsFixed(false); setIsDone(false); setActiveIndex(0); setProgress(0);
      } else if (rect.bottom > vh) {
        setIsFixed(true); setIsDone(false);
        const dist = rect.height - vh;
        let p = -rect.top / dist;
        p = Math.max(0, Math.min(1, p));
        setProgress(p);
        setActiveIndex(Math.min(features.length - 1, Math.floor(p * features.length)));
      } else {
        setIsFixed(false); setIsDone(true); setActiveIndex(features.length - 1); setProgress(1);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  let cls = 'sf-inner';
  if (isFixed) cls += ' sf-fixed';
  if (isDone) cls += ' sf-done';
  const feat = features[activeIndex];

  return (
    <div className="sf-container" ref={containerRef}>
      <div className={cls}>

        {/* ── Animated accent rings ── */}
        <div className="sf-rings">
          <div className="sf-ring r1" style={{ borderColor: `${feat.color}18` }} />
          <div className="sf-ring r2" style={{ borderColor: `${feat.accent}12` }} />
          <div className="sf-ring r3" style={{ borderColor: `${feat.color}0a` }} />
        </div>

        {/* ── Top progress bar ── */}
        <div className="sf-bar-track">
          <div className="sf-bar-fill" style={{
            width: `${progress * 100}%`,
            background: `linear-gradient(90deg, ${feat.color}, ${feat.accent})`
          }} />
        </div>

        {/* ── Left dots ── */}
        <nav className="sf-dots" aria-label="project navigation">
          {features.map((f, i) => (
            <div key={i} className="sf-dot-row">
              <span
                className={`sf-dot ${i === activeIndex ? 'on' : ''} ${i < activeIndex ? 'done' : ''}`}
                style={{
                  background: i <= activeIndex ? feat.color : undefined,
                  boxShadow: i === activeIndex ? `0 0 14px ${feat.color}55` : undefined
                }}
              />
              {i === activeIndex && (
                <span className="sf-dot-tag" style={{ color: feat.color }}>{f.badge}</span>
              )}
            </div>
          ))}
        </nav>

        {/* ── Main content ── */}
        <div className="sf-body">
          <p className="sf-label">
            <span className="sf-label-bar" style={{ background: feat.color }} />
            Featured Projects
            <span className="sf-label-bar" style={{ background: feat.color }} />
          </p>

          <div className="sf-stack">
            {features.map((f, i) => {
              const state = i === activeIndex ? 'on' : i < activeIndex ? 'prev' : 'next';
              return (
                <div key={f.id} className={`sf-card ${state}`}>

                  {/* icon */}
                  <div className="sf-icon-ring" style={{ borderColor: `${f.color}30` }}>
                    <div className="sf-icon-box" style={{ background: `${f.color}10`, color: f.color }}>
                      {icons[f.icon]}
                    </div>
                  </div>

                  <span className="sf-tag" style={{ color: f.color, background: `${f.color}0d`, borderColor: `${f.color}22` }}>
                    {f.badge}
                  </span>

                  <h3 className="sf-heading">{f.title}</h3>

                  <span className="sf-line" style={{ background: `linear-gradient(90deg, transparent, ${f.color}50, transparent)` }} />

                  <p className="sf-desc">{f.desc}</p>
                </div>
              );
            })}
          </div>

          {/* counter */}
          <div className="sf-num">
            <span className="sf-num-cur" style={{ color: feat.color }}>{String(activeIndex + 1).padStart(2, '0')}</span>
            <span className="sf-num-slash">/</span>
            <span className="sf-num-tot">{String(features.length).padStart(2, '0')}</span>
          </div>
        </div>

      </div>
    </div>
  );
}
